import * as React from 'react';
import { jsx, css } from '@emotion/react';
import Dexie from 'dexie';
import { setPersistence, browserLocalPersistence } from "firebase/auth";
import { auth, lineProvider, signInWithRedirect } from 'Src/firebase'
import { setTime, wait, hideLoading, showLoading } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { NonInteractiveContinueHandlerType, FeaturePropsType } from '../../../../../chat/views/types';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { RenderCtaButtonPropsType } from '../../../../../chat/views/CtaButton/index';
import { LineSvg } from './assets/Line.svg';

const { tablet, mobile } = mediaQueries();

export type CtaButtonPropsType = FeaturePropsType<RenderCtaButtonPropsType, NonInteractiveContinueHandlerType>
type AuthStateType = {
  status: 'initial' | 'loading' | 'error' | 'success';
  message: string;
};

export const CtaButton = (props: CtaButtonPropsType) => {
  const {
    userAnswers,
    beforeUnloadHandler,
    doesNotScroll,
    isInstant = false,
    continueHandler,
    waitTime = 0.4,
  } = props;
  const [isShow, setIsShow] = React.useState(isInstant);
  const [authState, setAuthState] = React.useState<AuthStateType>({
    status: 'initial',
    message: null,
  });

  const onClickHandler = React.useCallback(async () => {
    // リダイレクトのため離脱防止ダイアログを解除
    window.removeEventListener('beforeunload', beforeUnloadHandler)

    if (!auth || !lineProvider) {
      setAuthState({
        status: 'error',
        message: '認証に失敗しました。時間をおいて再度お試しください。',
      });
      return;
    }

    // ローディング表示
    showLoading();

    // LINE認証処理
    try {
      // CV情報を回答データのみにしてIndexedDBに格納
      const searchParams = new URLSearchParams(window.location.search)
      const userAnswerData = userAnswers.reduce((accumulator, data) => {
        accumulator[data.paramName] = data.answer
        return accumulator
      }, { cityId: null });
      const db = new Dexie('NurikaeProposalsDatabase');
      db.version(1).stores({ proposals: '++id,data' });
      await db.table('proposals').put({
        data: {
          part_type_id: 1, // 屋根のみの選択肢は存在しないため固定値
          city_id: userAnswerData.cityId,
          parts: {
            ...userAnswerData
          },
          marketing_attributes: {
            lp_url: window.location.href,
            original_params: window.location.search,
            utm_source: searchParams.get('utm_source'),
            utm_medium: searchParams.get('utm_medium'),
            utm_term: searchParams.get('utm_term'),
            utm_content: searchParams.get('utm_content'),
            utm_campaign: searchParams.get('utm_campaign'),
            referrer: searchParams.get('referrer') || document.referrer,
            ...Object.fromEntries(searchParams)
          }
        },
      });

      // LINE認証画面へリダイレクト
      await setPersistence(auth, browserLocalPersistence)
      await signInWithRedirect(auth, lineProvider)
    } catch (error) {
      setAuthState({
        status: 'error',
        message: error.code === 'auth/popup-closed-by-user' ? 'ログインがキャンセルされました' : '認証に失敗しました。時間をおいて再度お試しください。',
      });

      // ローディング非表示
      hideLoading();

      return;
    }
  }, [continueHandler]);

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(waitTime);

        setIsShow(true);
      }
    })();
  }, [isInstant, waitTime, continueHandler]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        padding: 30px 1em 0;
        position: relative;
        opacity: 0;
        bottom: -10px;
        transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

        ${isShow && css`
          opacity: 1;
          bottom: 0;
        `}
      `}
    >
      <div
        css={css`
          margin-bottom: 16px;

          ${autoResponsiveClamp('margin-bottom', 16, 14)}
        `}
      >
        <p
          css={css`
            color: #060606;
            font-size: 14px;
            text-align: center;

            ${autoResponsiveClamp('font-size', 14, 12)}
          `}
        >
          <a
            href="https://www.nuri-kae.jp/rule"
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              text-decoration: underline;
              display: inline;

              &:hover {
                text-decoration: none;
              }
            `}
          >
            利用規約
          </a>
          ・
          <a
            href="https://speee.jp/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              text-decoration: underline;
              display: inline;

              &:hover {
                text-decoration: none;
              }
            `}
          >
            プライバシーポリシー
          </a>
          に同意して
        </p>
      </div>
      <div
        css={css`
          max-width: 370px;
          margin-inline: auto;
        `}
      >
        <div
          css={css`
            padding-bottom: 2px;
          `}
        >
          <button
            type="button"
            id="ga_event_submit"
            onClick={onClickHandler}
            css={css`
              color: #fff;
              font-size: 22px;
              font-weight: bold;
              text-align: center;
              padding: 15px 3em;
              background-color: #06C755;
              border-radius: 6px;
              box-shadow: 0 2px 0 #109F4B;
              position: relative;
              transition: box-shadow ${setTime(0.2)}s, filter ${setTime(0.2)}s, transform ${setTime(0.2)}s;

              ${autoResponsiveClamp('font-size', 22, 18)}
              ${autoResponsiveClamp('padding-top', 15, 10)}
              ${autoResponsiveClamp('padding-bottom', 15, 10)}

              &:hover {
                box-shadow: 0 0 0 #109F4B;
                filter: brightness(1.03);
                transform: translateY(2px);
              }

              * {
                pointer-events: none;
              }
            `}
          >
            <span
              css={css`
                width: 40px;
                position: absolute;
                top: 50%;
                left: 1em;
                transform: translateY(-50%);

                ${autoResponsiveClamp('width', 40, 30)}
              `}
            >
              <LineSvg />
            </span>
            LINEで登録
          </button>
        </div>
      </div>
      {authState.status !== 'initial' && (
        <div
          css={css`
            max-width: 370px;
            margin-inline: auto;
            padding: 1em;
            background-color: #e8f5e8;
            border-radius: 6px;

            ${authState.status === 'loading' && css`
              background-color: #f0f8ff;
            `}

            ${authState.status === 'error' && css`
              background-color: #ffe6e6;
            `}
          `}
        >
          <p
            css={css`
              color: #006600;
              font-size: 14px;
              text-align: center;

              ${autoResponsiveClamp('font-size', 14, 12)}

              ${authState.status === 'loading' && css`
                color: #0066cc;
              `}

              ${authState.status === 'error' && css`
                color: #cc0000;
              `}
            `}
          >
            {authState.message}
          </p>
        </div>
      )}
    </div>
  );
};
